import React, { useEffect } from 'react';
import {
    FaUsers, FaCopy, FaWallet, FaUserCheck,
    FaCrown, FaShareAlt
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTheme } from '../../context/ThemeContext';
import { useUser } from '../../context/UserContext';
import api from '../../api/Api';

const MyReferral = () => {
    const { isDarkMode } = useTheme();
    const [loading, setLoading] = React.useState(true);
    const [referrals, setReferals] = React.useState([]);
    const { user } = useUser();

    const G = '#d4af37';
    const G2 = '#f0c842';
    const bg = isDarkMode ? '#0b0b0b' : '#f5f1e8';
    const card = isDarkMode ? '#151515' : '#ffffff';
    const border = isDarkMode ? 'rgba(212,175,55,.12)' : 'rgba(212,175,55,.2)';
    const text = isDarkMode ? '#f5f5f5' : '#111';
    const muted = isDarkMode ? 'rgba(255,255,255,.55)' : '#7b6d58';

    const referralCode = user?.USER?.my_code || 'N/A';
    const userData = user?.USER || {};

    useEffect(() => { fetchReferrals(); }, []);

    const fetchReferrals = async () => {
        try {
            setLoading(true);
            const { data } = await api.get('my-referral');
            if (data?.status) setReferals(data?.data || []);
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    };

    const copyCode = () => {
        navigator.clipboard.writeText(referralCode);
        toast.success('Referral code copied');
    };

    const stats = [
        { icon: <FaUsers />, label: 'My Referrals', value: userData?.my_referral ?? 0 },
        { icon: <FaUserCheck />, label: 'My Active', value: userData?.my_active ?? 0 },
        { icon: <FaWallet />, label: 'Direct Income', value: userData?.direct_income ?? 0 },
    ];

    return (
        <div style={{ minHeight: '100vh', background: bg ,}}>
            {/* ── Responsive CSS ── */}
            <style>{`
                .ref-container {
                    width: 100%;
                    padding: 16px;
                    box-sizing: border-box;
                }
                @media (min-width: 768px) {
                    .ref-container { padding: 24px 32px; }
                }

                /* Hero card */
                .ref-hero {
                    background: ${card};
                    border-radius: 20px;
                    border: 1px solid ${border};
                    padding: 20px;
                    margin-bottom: 20px;
                    position: relative;
                    overflow: hidden;
                    box-shadow: ${isDarkMode
                        ? '0 10px 40px rgba(0,0,0,.5)'
                        : '0 10px 40px rgba(212,175,55,.08)'};
                }
                @media (min-width: 768px) {
                    .ref-hero { padding: 28px 32px; }
                }

                /* Hero inner row */
                .ref-hero-inner {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 14px;
                    flex-wrap: wrap;
                }

                /* Code box */
                .ref-code-box {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    background: ${isDarkMode ? '#1a1a1a' : '#f8f8f8'};
                    border-radius: 12px;
                    border: 1px solid ${isDarkMode ? '#333' : '#e5e5e5'};
                    padding: 8px 12px;
                    flex-wrap: nowrap;
                    overflow: hidden;
                }

                /* Stats grid */
                .ref-stats {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 12px;
                    margin-bottom: 20px;
                }
                @media (max-width: 400px) {
                    .ref-stats { grid-template-columns: 1fr; }
                }

                .ref-stat-card {
                    background: ${card};
                    border-radius: 14px;
                    border: 1px solid ${border};
                    padding: 14px 12px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }

                /* Table wrapper */
                .ref-table-wrap {
                    background: ${card};
                    border-radius: 20px;
                    border: 1px solid ${border};
                    overflow: hidden;
                    margin-bottom: 24px;
                }

                .ref-table-header {
                    padding: 18px 20px;
                    border-bottom: 1px solid ${border};
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 10px;
                }

                /* Mobile card list (hidden on desktop) */
                .ref-mobile-list { display: block; }
                .ref-desktop-table { display: none; }
                @media (min-width: 640px) {
                    .ref-mobile-list { display: none; }
                    .ref-desktop-table { display: block; overflow-x: auto; }
                }

                .ref-user-card {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 14px 16px;
                    border-bottom: 1px solid ${border};
                    gap: 12px;
                }
                .ref-user-card:last-child { border-bottom: none; }
            `}</style>

            <div className="ref-container">

                {/* ── HERO ── */}
                <div className="ref-hero">
                    {/* top glow line */}
                    <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, height: 3,
                        background: `linear-gradient(90deg, transparent, ${G}, ${G2}, transparent)`,
                    }} />
                    {/* bg glow */}
                    <div style={{
                        position: 'absolute', top: -80, right: -80,
                        width: 200, height: 200, borderRadius: '50%',
                        background: 'rgba(212,175,55,.08)', filter: 'blur(50px)',
                        pointerEvents: 'none',
                    }} />

                    <div className="ref-hero-inner">
                        {/* Left: Title */}
                        <div>
                            <h4 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: text }}>
                                Refer & Earn
                            </h4>
                            <p style={{ margin: '2px 0 0', fontSize: 12, color: muted }}>
                                Invite friends to get rewards
                            </p>
                        </div>

                        {/* Right: Code + Buttons */}
                        <div className="ref-code-box">
                            <span style={{
                                fontSize: 14, fontWeight: 700, color: text,
                                fontFamily: 'monospace', letterSpacing: '1px',
                                whiteSpace: 'nowrap',
                            }}>
                                {referralCode}
                            </span>

                            <div style={{ width: 1, height: 22, background: isDarkMode ? '#333' : '#ddd', flexShrink: 0 }} />

                            {/* Copy */}
                            <button
                                onClick={copyCode}
                                title="Copy"
                                style={{
                                    width: 34, height: 34, borderRadius: 8,
                                    border: 'none', background: 'transparent',
                                    color: G, cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <FaCopy size={15} />
                            </button>

                            {/* Share */}
                            <button
                                onClick={async () => {
                                    try {
                                        if (navigator.share) {
                                            await navigator.share({
                                                title: 'Join me!',
                                                text: `Use my referral code: ${referralCode}`,
                                                url: `${window.location.origin}/register/${referralCode}`,
                                            });
                                        } else {
                                            await navigator.clipboard.writeText(window.location.href);
                                            toast.success('Link copied!');
                                        }
                                    } catch (err) { console.log('Share cancelled', err); }
                                }}
                                style={{
                                    padding: '0 14px', height: 34, borderRadius: 8,
                                    border: 'none', background: G, color: '#000',
                                    fontWeight: 700, fontSize: 13, cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', gap: 6,
                                    flexShrink: 0,
                                    boxShadow: `0 2px 8px ${G}40`,
                                }}
                            >
                                <FaShareAlt size={12} />
                                <span>Share</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* ── STATS ── */}
                <div className="ref-stats">
                    {stats.map((item, i) => (
                        <div key={i} className="ref-stat-card">
                            <div style={{
                                width: 38, height: 38, borderRadius: '50%',
                                background: 'rgba(212,175,55,.12)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0, color: G, fontSize: 15,
                            }}>
                                {item.icon}
                            </div>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 17, fontWeight: 800, color: G, lineHeight: 1.2 }}>
                                    {item.value}
                                </div>
                                <div style={{
                                    fontSize: 10, color: muted, marginTop: 2,
                                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                }}>
                                    {item.label}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* ── REFERRAL TABLE ── */}
                <div className="ref-table-wrap">
                    <div className="ref-table-header">
                        <div>
                            <div style={{ fontSize: 18, fontWeight: 800, color: text }}>
                                Referral History
                            </div>
                            <div style={{ fontSize: 12, color: muted, marginTop: 4 }}>
                                Your invited users and rewards
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: G, fontWeight: 700, fontSize: 13 }}>
                            <FaCrown /> Premium Referral Program
                        </div>
                    </div>

                    {/* ── MOBILE: Card List ── */}
                    <div className="ref-mobile-list">
                        {referrals.length === 0 ? (
                            <div style={{ padding: 24, textAlign: 'center', color: muted, fontSize: 13 }}>
                                No referrals yet
                            </div>
                        ) : referrals.map((item) => (
                            <div key={item.id} className="ref-user-card">
                                {/* Avatar + Name */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <div style={{
                                        width: 40, height: 40, borderRadius: '50%',
                                        background: `linear-gradient(135deg, ${G}, ${G2})`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0, overflow: 'hidden',
                                    }}>
                                        {item.profile_image
                                            ? <img src={item.profile_image} alt={item.name}
                                                style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} />
                                            : <span style={{ color: '#000', fontWeight: 900, fontSize: 16 }}>
                                                {item.name?.charAt(0).toUpperCase()}
                                            </span>
                                        }
                                    </div>
                                    <div>
                                        <div style={{ color: text, fontWeight: 700, fontSize: 14 }}>{item.name}</div>
                                        <div style={{ color: muted, fontSize: 11, marginTop: 2 }}>
                                            {item?.created_at
                                                ? new Date(item.created_at).toLocaleDateString()
                                                : 'N/A'}
                                        </div>
                                    </div>
                                </div>

                                {/* Status + Earnings */}
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                                    <span style={{
                                        padding: '4px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700,
                                        background: item.stake_status === 'Active'
                                            ? 'rgba(34,197,94,.12)' : 'rgba(245,158,11,.12)',
                                        color: item.stake_status === 'Active' ? '#22c55e' : '#f59e0b',
                                    }}>
                                        {item.stake_status}
                                    </span>
                                    <span style={{ color: G, fontWeight: 800, fontSize: 14 }}>
                                        {item.team_business}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* ── DESKTOP: Table ── */}
                    <div className="ref-desktop-table">
                        <table style={{ width: '100%', minWidth: 600, borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: isDarkMode ? 'rgba(212,175,55,.03)' : 'rgba(212,175,55,.05)' }}>
                                    {['User', 'Join Date', 'Status', 'Earnings'].map((h) => (
                                        <th key={h} style={{
                                            padding: '16px 18px', textAlign: 'left',
                                            color: muted, fontSize: 11,
                                            letterSpacing: 2, textTransform: 'uppercase', fontWeight: 700,
                                        }}>
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {referrals.map((item) => (
                                    <tr key={item.id} style={{ borderTop: `1px solid ${border}` }}>
                                        <td style={{ padding: 16 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                <div style={{
                                                    width: 42, height: 42, borderRadius: '50%',
                                                    background: `linear-gradient(135deg, ${G}, ${G2})`,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    overflow: 'hidden', flexShrink: 0,
                                                }}>
                                                    {item.profile_image
                                                        ? <img src={item.profile_image} alt={item.name}
                                                            style={{ width: 42, height: 42, borderRadius: '50%', objectFit: 'cover' }} />
                                                        : <span style={{ color: '#000', fontWeight: 900 }}>
                                                            {item.name?.charAt(0).toUpperCase()}
                                                        </span>
                                                    }
                                                </div>
                                                <span style={{ color: text, fontWeight: 700 }}>{item.name}</span>
                                            </div>
                                        </td>
                                        <td style={{ padding: 16, color: muted, fontSize: 14 }}>
                                            {item?.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A'}
                                        </td>
                                        <td style={{ padding: 16 }}>
                                            <span style={{
                                                padding: '5px 13px', borderRadius: 30,
                                                fontSize: 11, fontWeight: 700,
                                                background: item.stake_status === 'Active'
                                                    ? 'rgba(34,197,94,.12)' : 'rgba(245,158,11,.12)',
                                                color: item.stake_status === 'Active' ? '#22c55e' : '#f59e0b',
                                            }}>
                                                {item.stake_status}
                                            </span>
                                        </td>
                                        <td style={{ padding: 16, color: G, fontWeight: 800, fontSize: 15 }}>
                                            {item.team_business}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default MyReferral;